import type { InterpretationRule } from './rules.js';
import type { RelationshipContextType } from './relationshipContext.js';
import {
  RELATIONSHIP_CONTEXT_RULE_PACK_0_1_0,
  RELATIONSHIP_CONTEXT_TEMPLATE_PACK_0_1_0,
  type RelationshipRulePack,
  type RelationshipTemplatePack,
} from './relationshipInterpretation.js';
import { SRC_ZWDS_BOOK_001 } from './sourceRegistry.js';

function partnerAssistantRule(role: 'a' | 'b'): InterpretationRule {
  const label = role.toUpperCase();
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-PARTNER-${label}-ASSISTANTS-001`,
    version: '0.2.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 100,
    weight: 45,
    polarity: 'support',
    conflictGroup: 'partner-coordination-context',
    conditions: {
      any: [
        { featureExists: `relationship.${role}.focus.has_star.zuofu` },
        { featureExists: `relationship.${role}.focus.has_star.youbi` },
      ],
    },
    effects: [{
      dimension: 'partner_coordination_context',
      delta: 35,
      tags: ['traditional', 'relationship-context', 'partner', 'assistant-star'],
      claimKey: 'relationship.partner.coordination.assistants',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: '卷二・三妻妾・左輔右弼條',
    normalizedRationale: `古籍在夫妻宮脈絡中把左輔、右弼與較和順的伴侶互動連結；本規則只把 ${label} 方夫妻宮的輔弼視為協作／和緩的一項支持 Evidence，不推斷婚姻長久、配對成功或相合率。`,
  };
  return Object.freeze(rule);
}

function siblingZiweiRule(role: 'a' | 'b'): InterpretationRule {
  const label = role.toUpperCase();
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-SIBLINGS-${label}-ZIWEI-001`,
    version: '0.2.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 100,
    weight: 45,
    polarity: 'support',
    conflictGroup: 'sibling-support-context',
    conditions: { featureExists: `relationship.${role}.focus.has_star.ziwei` },
    effects: [{
      dimension: 'sibling_support_context',
      delta: 35,
      tags: ['traditional', 'relationship-context', 'siblings', 'major-star'],
      claimKey: 'relationship.siblings.support.ziwei',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: '卷二・二兄弟・紫微條',
    normalizedRationale: `古籍在兄弟宮紫微條有「倚靠」脈絡；本規則只把 ${label} 方兄弟宮紫微抽象為手足互動中較重支持／依靠的一項文化性 Evidence，不保證實際親密程度，也不沿用年長男性手足的限定。`,
  };
  return Object.freeze(rule);
}

export const RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_2_0: readonly InterpretationRule[] = Object.freeze([
  partnerAssistantRule('a'),
  partnerAssistantRule('b'),
]);

function partnerTaiyangTianliangAssistantsRule(role: 'a' | 'b'): InterpretationRule {
  const label = role.toUpperCase();
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-PARTNER-${label}-TAIYANG-TIANLIANG-ASSISTANTS-001`,
    version: '0.3.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 120,
    weight: 65,
    polarity: 'support',
    conflictGroup: 'partner-coordination-context',
    conditions: {
      all: [
        { featureExists: `relationship.${role}.focus.has_star.taiyang` },
        { any: [
          { featureExists: `relationship.${role}.focus.star.taiyang.brightness.temple` },
          { featureExists: `relationship.${role}.focus.star.taiyang.brightness.prosperous` },
          { featureExists: `relationship.${role}.focus.star.taiyang.brightness.gain` },
          { featureExists: `relationship.${role}.focus.star.taiyang.brightness.favorable` },
        ] },
        { featureExists: `relationship.${role}.focus.has_star.tianliang` },
        { any: [
          { featureExists: `relationship.${role}.focus.has_star.zuofu` },
          { featureExists: `relationship.${role}.focus.has_star.youbi` },
        ] },
      ],
    },
    effects: [{
      dimension: 'partner_coordination_context',
      delta: 45,
      tags: ['traditional', 'relationship-context', 'partner', 'compound', 'brightness', 'assistant-star'],
      claimKey: 'relationship.partner.coordination.taiyang_tianliang_assistants',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: '卷二・三妻妾・太陽條（太陽廟旺、與天梁同加左右）',
    normalizedRationale: `古籍把夫妻宮太陽廟旺、天梁與左右放在同一組合條件；本規則要求 ${label} 方夫妻宮同時具備太陽 supportive brightness、天梁與左輔／右弼，才增加伴侶協作／支援的一項複合 Evidence。移除原文性別、婚期、配偶品評與確定結果，不推斷婚姻成功或相合率。`,
  };
  return Object.freeze(rule);
}

export const RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_3_0: readonly InterpretationRule[] = Object.freeze([
  ...RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_2_0,
  partnerTaiyangTianliangAssistantsRule('a'),
  partnerTaiyangTianliangAssistantsRule('b'),
]);

function partnerTianliangCoordinationCounterRule(role: 'a' | 'b'): InterpretationRule {
  const label = role.toUpperCase();
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-PARTNER-${label}-TIANLIANG-COORDINATION-COUNTER-001`,
    version: '0.4.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 115,
    weight: 45,
    polarity: 'counter',
    conflictGroup: 'partner-coordination-context',
    conditions: {
      all: [
        { featureExists: `relationship.${role}.focus.has_star.tianliang` },
        { any: [
          { featureExists: `relationship.${role}.focus.has_star.qingyang` },
          { featureExists: `relationship.${role}.focus.has_star.tuoluo` },
          { featureExists: `relationship.${role}.focus.has_star.huoxing` },
          { featureExists: `relationship.${role}.focus.has_star.lingxing` },
          { featureExists: `relationship.${role}.focus.has_star.dikong` },
          { featureExists: `relationship.${role}.focus.has_star.dijie` },
        ] },
      ],
    },
    effects: [{
      dimension: 'partner_coordination_context',
      delta: -35,
      tags: ['traditional', 'relationship-context', 'partner', 'counter', 'compound'],
      claimKey: 'relationship.partner.coordination.tianliang_counter',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: '卷二・三妻妾・天梁條（天梁加羊陀火鈴空劫）',
    normalizedRationale: `古籍在夫妻宮天梁條把天梁遇羊、陀、火、鈴、空、劫與較不和順的互動脈絡連結；本規則只把 ${label} 方夫妻宮天梁同時見上述星曜之一，轉成伴侶協調阻力增加的一項 counter Evidence，不推斷分離、傷害或關係結果。`,
  };
  return Object.freeze(rule);
}

export const RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_4_0: readonly InterpretationRule[] = Object.freeze([
  ...RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_3_0,
  partnerTianliangCoordinationCounterRule('a'),
  partnerTianliangCoordinationCounterRule('b'),
]);

export const RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_2_0: readonly InterpretationRule[] = Object.freeze([
  siblingZiweiRule('a'),
  siblingZiweiRule('b'),
]);

function siblingWeakenedCoordinationRule(role: 'a' | 'b', starId: 'tianji' | 'taiyang'): InterpretationRule {
  const label = role.toUpperCase();
  const starLabel = starId === 'tianji' ? '天機' : '太陽';
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-SIBLINGS-${label}-${starId.toUpperCase()}-WEAKENED-001`,
    version: '0.3.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 110,
    weight: 45,
    polarity: 'counter',
    conflictGroup: 'sibling-coordination-context',
    conditions: {
      all: [
        { featureExists: `relationship.${role}.focus.has_star.${starId}` },
        { any: [
          { featureExists: `relationship.${role}.focus.star.${starId}.brightness.weak` },
          { featureExists: `relationship.${role}.focus.star.${starId}.brightness.fallen` },
        ] },
      ],
    },
    effects: [{
      dimension: 'sibling_coordination_context',
      delta: -35,
      tags: ['traditional', 'relationship-context', 'siblings', 'counter', 'brightness'],
      claimKey: 'relationship.siblings.coordination.weakened',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: `卷二・二兄弟・${starLabel}條`,
    normalizedRationale: `古籍在兄弟宮${starLabel}條把失陷狀態與較弱的和順／一致性連結；本規則只把 ${label} 方${starLabel}失陷抽象成協調一致性較弱的一項 counter Evidence，不推斷實際疏離、衝突結果或手足數量。`,
  };
  return Object.freeze(rule);
}

function siblingTianliangSupportiveCoordinationRule(role: 'a' | 'b'): InterpretationRule {
  const label = role.toUpperCase();
  const rule: InterpretationRule = {
    id: `RELATIONSHIP-SIBLINGS-${label}-TIANLIANG-SUPPORTIVE-001`,
    version: '0.4.0',
    topic: 'relationship',
    scopes: ['natal'],
    priority: 110,
    weight: 45,
    polarity: 'support',
    conflictGroup: 'sibling-coordination-context',
    conditions: {
      all: [
        { featureExists: `relationship.${role}.focus.has_star.tianliang` },
        { any: [
          { featureExists: `relationship.${role}.focus.star.tianliang.brightness.temple` },
          { featureExists: `relationship.${role}.focus.star.tianliang.brightness.prosperous` },
          { featureExists: `relationship.${role}.focus.star.tianliang.brightness.gain` },
          { featureExists: `relationship.${role}.focus.star.tianliang.brightness.favorable` },
        ] },
      ],
    },
    effects: [{
      dimension: 'sibling_coordination_context',
      delta: 35,
      tags: ['traditional', 'relationship-context', 'siblings', 'support', 'brightness'],
      claimKey: 'relationship.siblings.coordination.supportive',
    }],
    source: SRC_ZWDS_BOOK_001,
    sourceLocator: '卷二・二兄弟・天梁條',
    normalizedRationale: `古籍在兄弟宮天梁條以廟旺連結較和順的手足脈絡；本規則只把 ${label} 方天梁處於 supportive brightness 抽象為協調一致性較有支持的一項 Evidence，不推斷手足人數、同胞關係或現實關係結果。`,
  };
  return Object.freeze(rule);
}

export const RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_3_0: readonly InterpretationRule[] = Object.freeze([
  ...RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_2_0,
  siblingWeakenedCoordinationRule('a', 'tianji'),
  siblingWeakenedCoordinationRule('b', 'tianji'),
  siblingWeakenedCoordinationRule('a', 'taiyang'),
  siblingWeakenedCoordinationRule('b', 'taiyang'),
]);

export const RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_4_0: readonly InterpretationRule[] = Object.freeze([
  ...RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_3_0,
  siblingTianliangSupportiveCoordinationRule('a'),
  siblingTianliangSupportiveCoordinationRule('b'),
]);

export const RELATIONSHIP_TEMPLATE_PACK_0_2_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.2.0',
  templates: Object.freeze({
    'relationship.partner.coordination.assistants': Object.freeze({
      key: 'relationship.partner.coordination.assistants',
      variants: Object.freeze([
        '依《紫微斗數全書》夫妻宮的傳統脈絡，這組關係中至少一方的夫妻宮見左輔／右弼，可作為較容易形成協作、互相支援的一項文化性 Evidence；它不代表關係一定長久，也不是相合率。',
        '傳統紫微斗數在夫妻宮看到左輔、右弼時，會把它放進較和緩、能互相扶助的關係脈絡；本系統只把這點列為支持 Evidence，仍需保留雙方其他宮星與反向條件。',
        '至少一方的夫妻宮出現輔弼星，依古籍可提供較偏協作／支援的伴侶 Context；這是傳統命理規則的條件訊號，不等同現實關係成功與科學相容度。',
      ]),
    }),
    'relationship.siblings.support.ziwei': Object.freeze({
      key: 'relationship.siblings.support.ziwei',
      variants: Object.freeze([
        '依《紫微斗數全書》兄弟宮紫微條，這組手足 Context 中至少一方可見較偏依靠、支持的傳統訊號；本系統不把它直接等同實際親密程度。',
        '傳統兄弟宮脈絡把紫微與手足間的依靠感連結；本系統只將此條件轉成支持／承接的一項 Evidence，仍需和雙方其他條件交叉判讀。',
        '至少一方兄弟宮見紫微，可依古籍列為手足互動中較重支持與依靠的一項文化性 Context；它不等同對真實關係品質的科學評分。',
      ]),
    }),
  }),
});

export const RELATIONSHIP_TEMPLATE_PACK_0_3_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.3.0',
  templates: Object.freeze({
    ...RELATIONSHIP_TEMPLATE_PACK_0_2_0.templates,
    'relationship.siblings.coordination.weakened': Object.freeze({
      key: 'relationship.siblings.coordination.weakened',
      variants: Object.freeze([
        '依《紫微斗數全書》兄弟宮的傳統脈絡，至少一方的相關星曜處於較弱亮度時，可列為手足互動中協調一致性較弱的一項 counter Evidence；仍需與其他支持條件一起閱讀。',
        '傳統兄弟宮規則把部分星曜失陷視為較不利於一致互動的條件；本系統只把它保留為反向 Evidence，不把它寫成關係必然疏離或衝突。',
        '至少一方兄弟宮出現來源明確的失陷條件，可作為溝通／協調節奏較不一致的一項文化性 counter-context；它不是現實關係品質評分。',
      ]),
    }),
  }),
});

export const RELATIONSHIP_TEMPLATE_PACK_0_4_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.4.0',
  templates: Object.freeze({
    ...RELATIONSHIP_TEMPLATE_PACK_0_3_0.templates,
    'relationship.siblings.coordination.supportive': Object.freeze({
      key: 'relationship.siblings.coordination.supportive',
      variants: Object.freeze([
        '依《紫微斗數全書》兄弟宮天梁條，至少一方的天梁處於較有力的亮度時，可列為手足互動中較有利於和順協調的一項支持 Evidence；仍需與反向條件一起閱讀。',
        '傳統兄弟宮脈絡把天梁廟旺與較和順的互動條件連結；本系統只保留為協調一致性的一項支持 Evidence，不等同現實關係品質。',
        '至少一方兄弟宮的天梁處於 supportive brightness，可形成較偏協調／和順的一項文化性 context；這不是相合率，也不排除同時存在其他反向 Evidence。',
      ]),
    }),
  }),
});

export const RELATIONSHIP_TEMPLATE_PACK_0_5_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.5.0',
  templates: Object.freeze({
    ...RELATIONSHIP_TEMPLATE_PACK_0_4_0.templates,
    'relationship.partner.coordination.taiyang_tianliang_assistants': Object.freeze({
      key: 'relationship.partner.coordination.taiyang_tianliang_assistants',
      variants: Object.freeze([
        '依《紫微斗數全書》夫妻宮的組合脈絡，至少一方同時見太陽處於 supportive brightness、天梁與左輔／右弼，可列為伴侶互動中較有協作與互助條件的一項複合 Evidence；仍需與其他支持與反向條件一起閱讀。',
        '傳統夫妻宮規則把太陽廟旺、天梁與左右放在同一組合條件；本系統只將它轉成協作／支援 Context，不沿用性別、婚期或配偶評價，也不把它當成婚姻成功率。',
        '至少一方夫妻宮同時具備太陽較有力的亮度、天梁與輔弼時，可增加伴侶協調與互助的一項文化性 Evidence；這是來源可追溯的複合條件，不代表關係必然長久或科學相容度。',
      ]),
    }),
  }),
});

export const RELATIONSHIP_TEMPLATE_PACK_0_6_0: RelationshipTemplatePack = Object.freeze({
  id: 'TEMPLATE-RELATIONSHIP-ZHTW',
  version: '0.6.0',
  templates: Object.freeze({
    ...RELATIONSHIP_TEMPLATE_PACK_0_5_0.templates,
    'relationship.partner.coordination.tianliang_counter': Object.freeze({
      key: 'relationship.partner.coordination.tianliang_counter',
      variants: Object.freeze([
        '依《紫微斗數全書》夫妻宮天梁條，至少一方天梁同時見來源所列的阻力星曜時，可列為伴侶互動中協調節奏較不一致的一項 counter Evidence；仍需與其他支持條件一起閱讀。',
        '傳統夫妻宮脈絡把天梁與部分阻力星曜同見視為較不利於和順互動的條件；本系統只保留為協調阻力 Evidence，不推斷關係必然惡化。',
        '至少一方夫妻宮出現天梁與來源明確的阻力星曜組合，可增加伴侶協調需要磨合的一項文化性 counter-context；這不是相合率，也不是現實關係結果預測。',
      ]),
    }),
  }),
});

export const RELATIONSHIP_PARTNER_RULE_PACK_0_2_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-PARTNER',
  version: '0.2.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['partner']),
  rules: RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_2_0,
});

export const RELATIONSHIP_PARTNER_RULE_PACK_0_3_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-PARTNER',
  version: '0.3.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['partner']),
  rules: RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_3_0,
});

export const RELATIONSHIP_PARTNER_RULE_PACK_0_4_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-PARTNER',
  version: '0.4.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['partner']),
  rules: RELATIONSHIP_PARTNER_SOURCE_BACKED_RULES_0_4_0,
});

export const RELATIONSHIP_SIBLINGS_RULE_PACK_0_2_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-SIBLINGS',
  version: '0.2.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['siblings']),
  rules: RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_2_0,
});

export const RELATIONSHIP_SIBLINGS_RULE_PACK_0_3_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-SIBLINGS',
  version: '0.3.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['siblings']),
  rules: RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_3_0,
});

export const RELATIONSHIP_SIBLINGS_RULE_PACK_0_4_0: RelationshipRulePack = Object.freeze({
  id: 'INTERP-RELATIONSHIP-SIBLINGS',
  version: '0.4.0',
  compatibleCalculationRulesets: Object.freeze(['CALC-COMMON-BOOK']),
  compatibleRelationshipTypes: Object.freeze<RelationshipContextType[]>(['siblings']),
  rules: RELATIONSHIP_SIBLINGS_SOURCE_BACKED_RULES_0_4_0,
});

export function relationshipProductionPacks(type: RelationshipContextType): {
  rulePack: RelationshipRulePack;
  templatePack: RelationshipTemplatePack;
} {
  if (type === 'partner') return { rulePack: RELATIONSHIP_PARTNER_RULE_PACK_0_4_0, templatePack: RELATIONSHIP_TEMPLATE_PACK_0_6_0 };
  if (type === 'siblings') return { rulePack: RELATIONSHIP_SIBLINGS_RULE_PACK_0_4_0, templatePack: RELATIONSHIP_TEMPLATE_PACK_0_4_0 };
  return { rulePack: RELATIONSHIP_CONTEXT_RULE_PACK_0_1_0, templatePack: RELATIONSHIP_CONTEXT_TEMPLATE_PACK_0_1_0 };
}
